// Users management page (admin only)

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  AdminPanelSettings as AdminIcon,
  WorkOutline as WorkIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  CheckCircle as ActiveIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';

// Mock users data
const generateMockUsers = () => {
  const roles = ['admin', 'ordering_manager', 'employee', 'viewer'];
  const statuses = ['active', 'pending_verification', 'suspended', 'locked'];
  const departments = ['IT', 'HR', 'Księgowość', 'Marketing', 'Sprzedaż', 'Administracja'];
  
  const users = [
    {
      id: 'demo_user_admin',
      firstName: 'Jan',
      lastName: 'Kowalski',
      email: 'admin@demo.pl',
      role: 'admin',
      status: 'active',
      department: 'IT',
      lastLogin: new Date(Date.now() - 2 * 60 * 60 * 1000),
      createdAt: new Date('2024-01-15'),
      permissions: {
        canOrder: true,
        canEditStock: true,
        canViewReports: true,
        canManageUsers: true,
        canChangeSettings: true,
        canApproveOrders: true
      }
    },
    {
      id: 'demo_user_manager',
      firstName: 'Anna',
      lastName: 'Nowak',
      email: 'manager@demo.pl',
      role: 'ordering_manager',
      status: 'active',
      department: 'Administracja',
      lastLogin: new Date(Date.now() - 4 * 60 * 60 * 1000),
      createdAt: new Date('2024-01-20'),
      permissions: {
        canOrder: true,
        canEditStock: true,
        canViewReports: true,
        canManageUsers: false,
        canChangeSettings: false,
        canApproveOrders: true
      }
    }
  ];
  
  // Generate additional demo users
  const firstNames = ['Piotr', 'Maria', 'Tomasz', 'Katarzyna', 'Michał', 'Agnieszka', 'Rafał', 'Magdalena'];
  const lastNames = ['Wiśniewski', 'Wójcik', 'Kowalczyk', 'Kamiński', 'Lewandowski', 'Zieliński', 'Szymański'];
  
  for (let i = 3; i <= 12; i++) {
    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
    const role = roles[Math.floor(Math.random() * (roles.length - 1)) + 1]; // Exclude admin for random users
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    
    users.push({
      id: `user_${i}`,
      firstName,
      lastName,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@demo.pl`,
      role,
      status,
      department: departments[Math.floor(Math.random() * departments.length)],
      lastLogin: status === 'active' ? new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000) : new Date('2024-01-01'),
      createdAt: new Date(Date.now() - Math.random() * 180 * 24 * 60 * 60 * 1000),
      permissions: {
        canOrder: role !== 'viewer',
        canEditStock: ['admin', 'ordering_manager'].includes(role),
        canViewReports: role !== 'viewer',
        canManageUsers: role === 'admin',
        canChangeSettings: role === 'admin',
        canApproveOrders: ['admin', 'ordering_manager'].includes(role)
      }
    });
  }
  
  return users;
};

const mockUsers = generateMockUsers();

export default function UsersPage() {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuUser, setMenuUser] = useState<any>(null);

  // Ensure only admin users can access this page
  if (user?.role !== 'admin') {
    return (
      <Alert severity="error">
        <Typography variant="h6">Brak uprawnień</Typography>
        <Typography>Nie masz uprawnień do zarządzania użytkownikami.</Typography>
      </Alert>
    );
  }

  // Filter users
  const filteredUsers = useMemo(() => {
    return mockUsers.filter(u => {
      const matchesSearch = u.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           u.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           u.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = !roleFilter || u.role === roleFilter;
      const matchesStatus = !statusFilter || u.status === statusFilter;
      const matchesDepartment = !departmentFilter || u.department === departmentFilter;
      
      return matchesSearch && matchesRole && matchesStatus && matchesDepartment;
    });
  }, [searchTerm, roleFilter, statusFilter, departmentFilter]);

  // Stats
  const userStats = useMemo(() => {
    const total = mockUsers.length;
    const active = mockUsers.filter(u => u.status === 'active').length;
    const pending = mockUsers.filter(u => u.status === 'pending_verification').length;
    const admins = mockUsers.filter(u => u.role === 'admin').length;
    
    return { total, active, pending, admins };
  }, []);

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Administrator';
      case 'ordering_manager': return 'Menedżer zamówień';
      case 'employee': return 'Pracownik';
      case 'viewer': return 'Przeglądający';
      default: return role;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'ordering_manager': return 'warning';
      case 'employee': return 'primary';
      case 'viewer': return 'default';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Aktywny';
      case 'pending_verification': return 'Oczekuje weryfikacji';
      case 'suspended': return 'Zawieszony';
      case 'locked': return 'Zablokowany';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending_verification': return 'warning';
      case 'suspended': return 'error';
      case 'locked': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <ActiveIcon />;
      case 'pending_verification': return <WarningIcon />;
      case 'suspended': return <LockIcon />;
      case 'locked': return <LockIcon />;
      default: return <WarningIcon />;
    }
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setIsEditing(false);
    setUserDialogOpen(true);
  };

  const handleEditUser = (user: any) => {
    setSelectedUser(user);
    setIsEditing(true);
    setUserDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: any) => {
    setMenuAnchor(event.currentTarget);
    setMenuUser(user);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuUser(null);
  };

  const handleSaveUser = () => {
    // Mock save functionality
    alert('Funkcja zapisywania użytkownika zostanie zaimplementowana');
    setUserDialogOpen(false);
  };

  const departments = Array.from(new Set(mockUsers.map(u => u.department)));

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" fontWeight="bold">
            Zarządzanie użytkownikami
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="large"
            onClick={handleAddUser}
          >
            Dodaj użytkownika
          </Button>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          Zarządzaj użytkownikami i ich uprawnieniami w systemie
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <GroupIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {userStats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Łącznie użytkowników
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <ActiveIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" color="success.main">
                    {userStats.active}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Aktywni
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <WarningIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" color="warning.main">
                    {userStats.pending}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Oczekujący weryfikacji
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'error.main' }}>
                  <AdminIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" color="error.main">
                    {userStats.admins}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Administratorzy
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Szukaj użytkowników..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Rola</InputLabel>
                <Select
                  value={roleFilter}
                  label="Rola"
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <MenuItem value="">Wszystkie</MenuItem>
                  <MenuItem value="admin">Administrator</MenuItem>
                  <MenuItem value="ordering_manager">Menedżer zamówień</MenuItem>
                  <MenuItem value="employee">Pracownik</MenuItem>
                  <MenuItem value="viewer">Przeglądający</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">Wszystkie</MenuItem>
                  <MenuItem value="active">Aktywny</MenuItem>
                  <MenuItem value="pending_verification">Oczekuje weryfikacji</MenuItem>
                  <MenuItem value="suspended">Zawieszony</MenuItem>
                  <MenuItem value="locked">Zablokowany</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Dział</InputLabel>
                <Select
                  value={departmentFilter}
                  label="Dział"
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <MenuItem value="">Wszystkie</MenuItem>
                  {departments.map(dept => (
                    <MenuItem key={dept} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setRoleFilter('');
                  setStatusFilter('');
                  setDepartmentFilter('');
                }}
              >
                Wyczyść filtry
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Użytkownik</TableCell>
                <TableCell>Rola</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Dział</TableCell>
                <TableCell>Ostatnie logowanie</TableCell>
                <TableCell align="center">Akcje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ bgcolor: 'primary.light' }}>
                        {u.firstName[0]}{u.lastName[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {u.firstName} {u.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {u.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getRoleLabel(u.role)}
                      color={getRoleColor(u.role) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(u.status)}
                      label={getStatusLabel(u.status)}
                      color={getStatusColor(u.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{u.department}</TableCell>
                  <TableCell>
                    {u.lastLogin ? (
                      <Typography variant="body2">
                        {u.lastLogin.toLocaleDateString('pl-PL')}
                        <br />
                        <Typography component="span" variant="caption" color="text.secondary">
                          {u.lastLogin.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Nigdy
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, u)}
                    >
                      <MoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditUser(menuUser)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edytuj</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <EmailIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Wyślij email</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <LockIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Zresetuj hasło</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Usuń</ListItemText>
        </MenuItem>
      </Menu>

      {/* User Dialog */}
      <Dialog
        open={userDialogOpen}
        onClose={() => setUserDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isEditing ? 'Edytuj użytkownika' : 'Dodaj nowego użytkownika'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Imię"
                defaultValue={selectedUser?.firstName || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nazwisko"
                defaultValue={selectedUser?.lastName || ''}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                defaultValue={selectedUser?.email || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Rola</InputLabel>
                <Select
                  defaultValue={selectedUser?.role || 'employee'}
                  label="Rola"
                >
                  <MenuItem value="admin">Administrator</MenuItem>
                  <MenuItem value="ordering_manager">Menedżer zamówień</MenuItem>
                  <MenuItem value="employee">Pracownik</MenuItem>
                  <MenuItem value="viewer">Przeglądający</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Dział</InputLabel>
                <Select
                  defaultValue={selectedUser?.department || ''}
                  label="Dział"
                >
                  {departments.map(dept => (
                    <MenuItem key={dept} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Uprawnienia
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={<Switch defaultChecked={selectedUser?.permissions?.canOrder} />}
                    label="Może składać zamówienia"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={<Switch defaultChecked={selectedUser?.permissions?.canEditStock} />}
                    label="Może edytować stany"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={<Switch defaultChecked={selectedUser?.permissions?.canViewReports} />}
                    label="Może przeglądać raporty"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={<Switch defaultChecked={selectedUser?.permissions?.canApproveOrders} />}
                    label="Może zatwierdzać zamówienia"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={<Switch defaultChecked={selectedUser?.permissions?.canManageUsers} />}
                    label="Może zarządzać użytkownikami"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={<Switch defaultChecked={selectedUser?.permissions?.canChangeSettings} />}
                    label="Może zmieniać ustawienia"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>
            Anuluj
          </Button>
          <Button variant="contained" onClick={handleSaveUser}>
            {isEditing ? 'Zapisz zmiany' : 'Dodaj użytkownika'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}